import React, { useContext, useEffect, useState } from 'react';
import { useApp } from '../../context/app-context';
import { useRoute } from '../../context/route-context';
import getLeftNavItems, { getProfileInfo } from '../../service/left-navigation-service';
import sanitizeHtml from 'sanitize-html';
import { get, map, reduce } from 'lodash';
import { css } from '@emotion/css';
import { useAuth } from '../../context/auth-context-aem';
import { isMobileScreen } from '../../utils/utils';
import { useViewport } from '../../context/view-port-provider';

const leftNavWrapper = css`
  margin-left: 25px;
  margin-top: 40px;
`;
const leftNavTitle = css`
  font-size: 20px;
  font-weight: 600;
`;
const leftNav = css`
  margin-top: 10px;
`;
const leftNavGroupWrapper = css`
  display: flex;
`;
const leftNavIcon = css`
  padding: 5px;
  height: 35px;
  width: 35px;
`;
const leftNavGroupTitle = css`
  margin-top: 4px;
  margin-bottom: 0;
  font-size: 16px;
  font-weight: 600;
  padding: 0 5px;
`;
const leftNavList = css`
  list-style: none;
  padding: 8px 8px 8px 0;

  & a.active {
    text-decoration: underline;
    color: #60151e;
  }

  & a:hover {
    color: #60151e;
  }
`;

const leftNavMobileList = css`
  & > li {
    & a.active {
      text-decoration: underline;
      color: #7b1a27;
    }

    & a.active:hover {
      color: #60151e;
    }
  }
`;

const leftNavListBadge = css`
  background: #fbe7e4;
  border-radius: 4px;
  margin-left: 10px;
  padding: 1px 4px;
  font-weight: 400;
  font-size: 0.75rem;
`;

type NavState = {
  navData?: any;
  navTitle?: any;
  leftnav?: any;
  selectedNav?: any;
};

export function UcaNavigation({}): JSX.Element {
  const { appData } = useApp();
  const { isMobileScreenCheck } = useViewport();
  const currentAuthContext = useAuth();
  const user = get(currentAuthContext, 'user');
  const { routeData } = useRoute();

  const [navItems, setNavItems] = useState<NavState>({});
  const [navItemsV2, setNavItemsV2] = useState<any>({});
  const [currentState, setCurrentState] = useState<any>('LEFT_NAV_MENU');

  useEffect(() => {
    const enableAcctDashV2 = get(window, 'SWY.CONFIGSERVICE.featureToggleConfig.enableAcctDashV2', false);

    // Always fetch data from the API

    //Conditionally fetch data from the API to display redesigned left nav
    if (enableAcctDashV2) {
      fetchLeftNavMenuV2();
    } else {
      fetchLeftNavMenu();
    }
  }, [routeData, isMobileScreenCheck]);

  const fetchLeftNavMenu = async () => {
    try {
      const response = await getLeftNavItems(appData.environmentVariables.LEFT_NAV_API);
      setLeftnavItems(response.data ?? {});
      setCurrentState('LEFT_NAV_MENU');
    } catch (e: any) {
      console.error('error', e.message);
    }
  };

  const fetchLeftNavMenuV2 = async () => {
    let profileData = {};
    try {
      const response = await getLeftNavItems(appData.environmentVariables.LEFT_NAV_API_V2);
      if (!isMobileScreen()) {
        try {
          profileData = await getProfileInfo(appData.environmentVariables, user);
        } catch (e: any) {
          console.error('Error fetching profile info:', e.message);
        }
        setLeftnavItemsV2(response.data, profileData);
      } else {
        const customeResponse = reduce(
          get(response, 'data.leftNavGroupCollection', {}),
          (acc: any, item) => {
            acc = [...acc, ...item.menuItems];
            return acc;
          },
          [],
        );
        customeResponse.unshift({
          leftNavText: get(response, 'data.panelTitle', ''),
          leftNavLink: get(response, 'data.panelLink', ''),
          eotDrivenLink: false,
          eotFlagName: null,
          addLabel: null,
        });
        const leftNavResponse = customeResponse.map(({ leftNavLink, leftNavText, ...rest }) => ({
          link: leftNavLink,
          linktitle: leftNavText,
          ...rest,
        }));
        setLeftnavItems({ leftnav: leftNavResponse });
      }
    } catch (e: any) {
      console.error('error', e.message);
    }
  };

  const shouldAddPersonalInfoLabel = (
    profileData: { data: { profileCompletionPercentage: any; addresses: any } },
    isProfileDataAvailable: boolean,
  ) => {
    if (!isProfileDataAvailable) return false;

    const profileCompletionPercentage = get(profileData, 'data.profileCompletionPercentage');
    const addresses = get(profileData, 'data.addresses');

    let showAddLabel = profileData && profileCompletionPercentage !== '100';
    if (profileData && addresses) {
      if (profileCompletionPercentage === '100') {
        showAddLabel = false;
      } else if (profileCompletionPercentage !== '80') {
        showAddLabel = true;
      }
    }
    return showAddLabel;
  };

  const setLeftnavItemsV2 = (navData: any = {}, profileData: any = {}) => {
    const isProfileDataAvailable = !!get(profileData, 'data');
    const shouldAddAddressLabel = isProfileDataAvailable && !(get(profileData, 'data.addresses.length', 0) > 0);

    const addLabelToMenuItem = (menuItem: any, profileData: any) => {
      const lowerCaseText = get(menuItem, 'leftNavText', '').toLowerCase();
      switch (lowerCaseText) {
        case 'addresses':
          return shouldAddAddressLabel;
        case 'personal info':
          return shouldAddPersonalInfoLabel(profileData, isProfileDataAvailable);
        default:
          return false;
      }
    };

    const leftMenuItems = map(get(navData, 'leftNavGroupCollection', []), (group: any) => {
      const menuItems = map(get(group, 'menuItems', []), (menuItem: any) => {
        const isActive = get(menuItem, 'leftNavLink', '')
          ? get(menuItem, 'leftNavText', '').toLowerCase().includes(routeData.currentPage.toLowerCase())
          : false;
        const addLabel = addLabelToMenuItem(menuItem, profileData);
        return {
          link: get(menuItem, 'leftNavLink', '#') || '#',
          linkText: get(menuItem, 'leftNavText'),
          isActive,
          addLabel,
        };
      });
      return {
        menuItems,
        groupIcon: get(group, 'groupIcon'),
        groupTitle: get(group, 'groupTitle'),
      };
    });

    setNavItemsV2({
      navData,
      leftnav: leftMenuItems,
      navTitle: get(navData, 'panelTitle'),
      selectedNav: routeData.currentPage,
    });
    setCurrentState('LEFT_NAV_MENU_V2');
  };

  const setLeftnavItems = (navData) => {
    const leftMenuItems =
      navData &&
      navData.leftnav.map((navItem) =>
        navItem.linktitle.toLowerCase() == routeData.currentPage.toLowerCase()
          ? {
              ...navItem,
              link: `${navItem.link}`,
              linkText: navItem.linktitle,
              isActive: true,
            }
          : {
              ...navItem,
              link: `${navItem.link}.html`,
              linkText: navItem.linktitle,
              isActive: false,
            },
      );
    setNavItems({
      navData,
      leftnav: leftMenuItems,
      navTitle: navData.navtitle,
      selectedNav: routeData.currentPage,
    });
  };
  const renderLeftNavMobile = () => (
    <div className="uca-navigation aem-GridColumn aem-GridColumn--default--12">
      <div className="d-block d-md-block d-lg-none show-mobile">
        <div className="simple-page-title section">
          <div style={{ marginLeft: '0px' }}>
            <div>
              <div className="title-bg">
                <h2 className="uca-title-mobile">{navItems.navTitle}</h2>
              </div>
            </div>
          </div>
        </div>
        <div className="product-level-4 d-block d-md-block show-mobile">
          <h2 className="sub-banner d-none">
            <a
              href="https://www-qa1.tomthumb.com/account/account-settings.html"
              className="active"
              aria-current="location"
            >
              {'Account Settings'}
            </a>
          </h2>
          <div className="dropdown left-nav-mobile-container" aria-label="secondary navigation">
            <button
              className="btn btn-default dropdown-toggle nav-item-left d-block d-md-block"
              type="button"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <span className="float-left" id="paymentMethodSelectText">
                {'Manage Schedule & Save'}
              </span>
              <span
                className="svg-icon svg-icon-Arrow_down_black float-right dropdown-caret-leftnav"
                role="img"
                aria-hidden="true"
              ></span>
            </button>
            <ul className={`dropdown-menu ${leftNavMobileList}`}>
              {navItems.leftnav &&
                navItems.leftnav.map((navItem, index) => {
                  const isActive = navItem.link && navItem.link.indexOf('schedule-and-save') !== -1;
                  return (
                    <li key={index} className={`list-group-item mobile-list-styling`}>
                      <a
                        className={`user-protected-link user-prompt-a user-prompt-g user-prompt-r ${isActive ? 'active' : ''}`}
                        href={sanitizeHtml(navItem.link)}
                      >
                        {navItem.linktitle}
                      </a>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLeftNavMenu = () => (
    <div className="acc-page d-none d-md-none d-lg-block hide-mobile">
      <div className="panel-group d-block d-md-none menu-tree" id="accordion">
        <div className="panel panel-default list-grp-bck">
          <div
            className="panel-heading accordion-toggle collapsed"
            data-toggle="collapse"
            data-parent="#accordion"
            data-target="#collapseOne"
            aria-expanded="false"
          >
            <h4 className="panel-title">
              <span>{navItems.navTitle}</span>
            </h4>
          </div>
          <div id="collapseOne" className="panel-collapse collapse" aria-expanded="false" style={{ height: '0px' }}>
            <ul className="list-group list-grp-bck">
              {navItems.leftnav &&
                navItems.leftnav.map((navItem, index) => {
                  return (
                    <li key={index}>
                      <a
                        href={sanitizeHtml(navItem.link)}
                        className="user-protected-link user-prompt-a user-prompt-g user-prompt-r"
                      >
                        {navItem.linktitle}
                      </a>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      </div>
      <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 rewards d-none d-md-block hide-mobile">
        <div className="navbar-collapse" id="myNavbar" aria-expanded="false">
          <nav className="menu-tree" aria-label="secondary navigation">
            <h2>{navItems.navTitle}</h2>
            <ul className="nav navbar-nav">
              {navItems.leftnav &&
                navItems.leftnav.map((navItem, index) => {
                  const isActive = navItem.link && navItem.link.indexOf('schedule-and-save') !== -1;
                  return (
                    <li key={index}>
                      <a
                        href={sanitizeHtml(navItem.link)}
                        className={`user-protected-link user-prompt-a user-prompt-g user-prompt-r ${isActive ? 'active' : ''}`}
                      >
                        {navItem.linktitle}
                      </a>
                    </li>
                  );
                })}
            </ul>
          </nav>
        </div>
      </div>
    </div>
  );

  const renderLeftNavMenuV2 = () => (
    <div className="acc-page d-none d-md-none d-lg-block hide-mobile">
      <div>
        <div className={leftNavWrapper}>
          <a className={leftNavTitle} href={navItemsV2?.navData?.panelLink}>
            {navItemsV2?.navTitle}
          </a>
          <div className={leftNav}>
            {map(navItemsV2.leftnav, ({ groupIcon, groupTitle, menuItems }, index) => (
              <div>
                <div>
                  <div className={leftNavGroupWrapper}>
                    <img className={leftNavIcon} src={groupIcon} alt={groupTitle} />
                    <p className={leftNavGroupTitle}>{groupTitle}</p>
                  </div>
                  <ul>
                    {map(menuItems, ({ link, linkText, isActive, addLabel }, index) => (
                      <li key={index} className={leftNavList}>
                        <a
                          href={sanitizeHtml(link)}
                          className={`user-protected-link user-prompt-a user-prompt-g user-prompt-r ${isActive ? 'active' : ''}`}
                        >
                          {linkText}
                        </a>
                        {/* {item?.addLabel && <p className={`${leftNavListBadge} d-inline-block mb-0`}>Add</p>} */}
                        {addLabel && <p className={`${leftNavListBadge} d-inline-block mb-0`}>Add</p>}
                      </li>
                    ))}
                  </ul>
                </div>
                {Number(index) < navItemsV2?.leftnav?.length - 1 && <hr className="hr-line" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (currentState) {
      case 'LEFT_NAV_MENU':
        return renderLeftNavMenu();
      case 'LEFT_NAV_MENU_V2':
        return renderLeftNavMenuV2();
      default:
        return renderLeftNavMenu();
    }
  };
  return (
    <div className="leftNav-wrapper">
      {renderContent()}
      {renderLeftNavMobile()}
    </div>
  );
}
